// =====================================================================
// AccidentsSecouristes.jsx — M-010 : qui peut donner les premiers soins, et les autres formations
// =====================================================================
// 23-10-2026 (demandes Silviu). Onglet « Secouristes & formations » du module Accidents. Celui qui en
// a le droit (safety.manage.all, Vincent) note la séance prévue, puis la formation faite, et joint le
// scan du certificat. Secourisme : Zeus en déduit la validité (recyclage annuel de 4 h, tous les 2 ans
// avec la dérogation et son analyse de risque, art. I.5-9) ; seuls les secouristes « valables » sont
// proposés dans la déclaration d'un accident. Autres formations (cariste…) : la fin de validité est
// celle du certificat. Les responsables sont prévenus 60 puis 30 jours avant chaque échéance.
// =====================================================================

import { useState, useEffect, useCallback } from 'react';
import { safety, errMsg } from '../services/api.js';
import { TYPES_AUTRES, pastille, enClair, phraseStatut, erreurFiche } from '../lib/secouristes.js';

const C = {
  line: '#e5e7eb', mute: '#7b8494', brand: '#714B67', crit: '#dc2626',
  okBg: '#ecfdf5', okLine: '#a7f3d0', warnBg: '#fffbeb', warnLine: '#fde68a', critBg: '#fef2f2',
};
const card = { background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, padding: '0.9rem 1rem', marginBottom: '0.8rem' };
const inp = { width: '100%', boxSizing: 'border-box', padding: '0.55rem 0.6rem', border: `1px solid ${C.line}`, borderRadius: 6, fontSize: '0.95rem', background: '#fff' };
const lbl = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', margin: '0 0 4px' };
const titre = { fontSize: '1.05rem', color: C.brand, margin: '1.2rem 0 0.6rem' };
const btn = (bg, fg = '#fff') => ({
  border: 'none', borderRadius: 6, padding: '0.55rem 0.9rem', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', background: bg, color: fg,
});
const DIX_MO = 10 * 1024 * 1024;
const MSG_INTITULE = 'Pour « Autre formation », écrivez laquelle (ex. nacelle, transpalette électrique).';

/** Une note longue (ex. celle des 5 désignés du 26/08) tient en deux lignes ; le texte entier est dans « Modifier ». */
const court = (t, n = 110) => (t.length > n ? `${t.slice(0, n).trimEnd()}…` : t);

function Pastille({ s }) {
  const p = pastille(s);
  return (
    <span style={{ background: p.bg, color: p.fg, borderRadius: 999, padding: '2px 9px', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {p.label}
    </span>
  );
}

export default function AccidentsSecouristes({ canManage }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [flash, setFlash] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edition, setEdition] = useState(null);   // la fiche ouverte (copie des champs modifiables)
  const [retrait, setRetrait] = useState(null);   // { id, name, motif }
  const [ajout, setAjout] = useState('');
  const [ajoutAutre, setAjoutAutre] = useState({ userId: '', type: 'CARISTE', intitule: '' });

  const charger = useCallback(async () => {
    try { setData(await safety.secouristes()); } catch (e) { setErr(errMsg(e)); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const ouvrir = (s) => {
    setRetrait(null); setErr(null);
    setEdition({
      id: s.id, name: s.name, type: s.type,
      faiteLe: s.faiteLe || '', prevueLe: s.prevueLe || '',
      valableJusquAu: s.type === 'SECOURISME' ? '' : (s.valableJusquAu || ''),
      organisme: s.organisme || '', note: s.note || '',
      derogation2ans: Boolean(s.derogation2ans), analyseRisque: s.analyseRisque || '',
      prevueAvant: s.prevueLe || null,
    });
  };
  const champ = (k) => (e) => setEdition((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const enregistrer = async (e) => {
    e.preventDefault();
    const pb = erreurFiche(edition, data.aujourdhui, edition.prevueAvant);
    if (pb) { setErr(pb); return; }
    setBusy(true); setErr(null); setFlash(null);
    try {
      const corps = {
        faiteLe: edition.faiteLe || null, prevueLe: edition.prevueLe || null,
        organisme: edition.organisme || null, note: edition.note || null,
      };
      if (edition.type === 'SECOURISME') Object.assign(corps, { derogation2ans: edition.derogation2ans, analyseRisque: edition.analyseRisque || null });
      else corps.valableJusquAu = edition.valableJusquAu || null;
      const s = await safety.modifierSecouriste(edition.id, corps);
      setEdition(null);
      setFlash(`${s.name} : ${pastille(s).label}.`);
      await charger();
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  const ajouter = async (corps) => {
    setBusy(true); setErr(null); setFlash(null);
    try {
      const s = await safety.ajouterSecouriste(corps);
      await charger();
      ouvrir(s);
      setFlash(`${s.name} : ${s.type === 'SECOURISME' ? 'dans la liste des secouristes' : `« ${s.intitule} » ajoutée`}. Notez la séance prévue, ou la formation si elle est déjà faite.`);
      return true;
    } catch (e2) { setErr(errMsg(e2)); return false; } finally { setBusy(false); }
  };

  const retirer = async (e) => {
    e.preventDefault();
    const motif = (retrait.motif || '').trim();
    if (motif.length < 3) { setErr('Écrivez en quelques mots pourquoi cette fiche quitte la liste.'); return; }
    setBusy(true); setErr(null); setFlash(null);
    try {
      await safety.retirerSecouriste(retrait.id, motif);
      setFlash(`${retrait.name} : la fiche n’est plus dans la liste (elle reste dans l’historique).`);
      setRetrait(null);
      await charger();
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  const envoyer = async (s, file) => {
    if (!file) return;
    if (file.size > DIX_MO) { setErr('Fichier trop lourd : 10 Mo au plus.'); return; }
    setBusy(true); setErr(null); setFlash(null);
    try {
      await safety.envoyerCertificat(s.id, file);
      setFlash(`Certificat joint : ${s.name}${s.type === 'SECOURISME' ? '' : ` — ${s.intitule}`}.`);
      await charger();
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  if (!data) return <div style={card}>{err || 'Chargement…'}</div>;
  const probleme = edition ? erreurFiche(edition, data.aujourdhui, edition.prevueAvant) : null;
  const intituleManque = ajoutAutre.type === 'AUTRE' && ajoutAutre.intitule.trim().length < 2;

  const carte = (s) => (
    <div key={s.id} style={card}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
        <b style={{ fontSize: '1rem' }}>{s.name}</b>
        {s.type !== 'SECOURISME' && <span style={{ fontSize: '0.88rem', color: '#374151' }}>{s.intitule}</span>}
        <Pastille s={s} />
        {!s.actif && <span style={{ fontSize: '0.8rem', color: C.crit }}>compte désactivé{s.type === 'SECOURISME' ? ' : n’est plus proposé' : ''}</span>}
      </div>
      <div style={{ fontSize: '0.9rem', marginBottom: 4 }}>{phraseStatut(s, data.aujourdhui)}</div>
      <div style={{ fontSize: '0.82rem', color: C.mute }}>
        {s.type === 'SECOURISME' ? 'Formation ou dernier recyclage' : 'Formation faite le'} : {enClair(s.faiteLe) || '—'} · Organisme : {s.organisme || '—'}
        {' · '}Certificat : {s.certificat
          ? <a href={safety.certificatUrl(s.id)} target="_blank" rel="noreferrer">voir</a>
          : 'pas encore joint'}
      </div>
      {s.type === 'SECOURISME' && s.derogation2ans && (
        <div style={{ fontSize: '0.82rem', color: C.mute }}>Dérogation 2 ans — analyse de risque : {s.analyseRisque}</div>
      )}
      {s.note && <div title={s.note} style={{ fontSize: '0.8rem', color: C.mute, marginTop: 4, whiteSpace: 'pre-wrap' }}>{court(s.note)}</div>}

      {canManage && edition?.id !== s.id && retrait?.id !== s.id && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          <button type="button" disabled={busy} onClick={() => ouvrir(s)} style={btn(C.brand)}>Modifier</button>
          <label style={{ ...btn('#eef2ff', '#4338ca'), opacity: busy ? 0.6 : 1 }}>
            {s.certificat ? 'Remplacer le certificat' : 'Joindre le certificat'}
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" hidden disabled={busy}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; envoyer(s, f); }} />
          </label>
          <button type="button" disabled={busy} style={btn('#f3f4f6', '#374151')}
            onClick={() => { setEdition(null); setErr(null); setRetrait({ id: s.id, name: s.name, motif: '' }); }}>
            Retirer de la liste
          </button>
        </div>
      )}

      {canManage && edition?.id === s.id && (
        // noValidate : c'est NOTRE message qui s'affiche (en clair), pas l'infobulle du navigateur.
        <form noValidate onSubmit={enregistrer} style={{ marginTop: 12, display: 'grid', gap: 10 }}>
          <div>
            <label style={lbl}>{s.type === 'SECOURISME' ? 'Formation ou dernier recyclage faits le' : 'Formation faite le'}</label>
            <input type="date" style={inp} value={edition.faiteLe} max={data.aujourdhui} onChange={champ('faiteLe')} />
          </div>
          {s.type !== 'SECOURISME' && (
            <div>
              <label style={lbl}>Valable jusqu’au (date écrite sur le certificat, vide s’il n’y en a pas)</label>
              <input type="date" style={inp} value={edition.valableJusquAu} min={edition.faiteLe || undefined} onChange={champ('valableJusquAu')} />
            </div>
          )}
          <div>
            <label style={lbl}>Prochaine séance prévue le</label>
            <input type="date" style={inp} value={edition.prevueLe} min={data.aujourdhui} onChange={champ('prevueLe')} />
          </div>
          <div>
            <label style={lbl}>Organisme</label>
            <input type="text" style={inp} value={edition.organisme} maxLength={120} placeholder="ex. Mensura, A-First, Croix-Rouge" onChange={champ('organisme')} />
          </div>
          {s.type === 'SECOURISME' && (
            <div>
              <label style={{ fontSize: '0.9rem', display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={edition.derogation2ans} onChange={champ('derogation2ans')} />
                Dérogation : recyclage tous les 2 ans
              </label>
              {edition.derogation2ans && (
                <div style={{ marginTop: 6 }}>
                  <label style={lbl}>Où est l’analyse de risque qui la permet ?</label>
                  <input type="text" style={inp} value={edition.analyseRisque} maxLength={300}
                    placeholder="ex. classeur prévention, onglet 3" onChange={champ('analyseRisque')} />
                  <div style={{ fontSize: '0.78rem', color: C.mute, marginTop: 4 }}>
                    Art. I.5-9 : seulement si l’analyse de risque le permet et qu’elle est tenue à disposition du contrôle.
                  </div>
                </div>
              )}
            </div>
          )}
          <div>
            <label style={lbl}>Note</label>
            <textarea style={{ ...inp, minHeight: 56 }} value={edition.note} maxLength={500} onChange={champ('note')} />
          </div>
          {probleme && <div style={{ color: '#991B1B', fontSize: '0.86rem' }}>{probleme}</div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="submit" disabled={busy || Boolean(probleme)} style={btn(C.brand)}>
              {busy ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button type="button" disabled={busy} onClick={() => { setEdition(null); setErr(null); }} style={btn('#f3f4f6', '#374151')}>Annuler</button>
          </div>
        </form>
      )}

      {canManage && retrait?.id === s.id && (
        <form noValidate onSubmit={retirer} style={{ marginTop: 12, display: 'grid', gap: 10 }}>
          <div>
            <label style={lbl}>Pourquoi cette fiche quitte la liste ?</label>
            <input type="text" style={inp} value={retrait.motif} maxLength={200} placeholder="ex. a quitté l’entreprise"
              onChange={(e) => setRetrait((r) => ({ ...r, motif: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="submit" disabled={busy || (retrait.motif || '').trim().length < 3} style={btn(C.crit)}>Confirmer le retrait</button>
            <button type="button" disabled={busy} onClick={() => { setRetrait(null); setErr(null); }} style={btn('#f3f4f6', '#374151')}>Annuler</button>
          </div>
        </form>
      )}
    </div>
  );

  return (
    <div>
      {data.valables === 0 ? (
        <div style={{ ...card, background: C.warnBg, borderColor: C.warnLine, color: '#92400E' }}>
          <b>Aucun secouriste valable aujourd’hui.</b> À la déclaration d’un accident, seule l’option
          « je me suis soigné moi-même » est proposée. Dès qu’une formation est faite, notez-la ici.
        </div>
      ) : (
        <div style={{ ...card, background: C.okBg, borderColor: C.okLine, color: '#065F46' }}>
          <b>{data.valables} secouriste{data.valables > 1 ? 's' : ''} valable{data.valables > 1 ? 's' : ''}</b> — proposé
          {data.valables > 1 ? 's' : ''} dans la déclaration d’un accident.
        </div>
      )}

      {err && <div style={{ ...card, background: C.critBg, borderColor: '#f6cfcf', color: '#991B1B' }}>{err}</div>}
      {flash && <div style={{ ...card, background: C.okBg, borderColor: C.okLine, color: '#065F46', fontWeight: 600 }}>{flash}</div>}

      <h2 style={titre}>Secouristes</h2>
      {!data.secouristes.length && <div style={{ ...card, color: C.mute }}>Personne n’est encore inscrit comme secouriste.</div>}
      {data.secouristes.map(carte)}
      {canManage && (
        <div style={{ ...card, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select style={{ ...inp, flex: '1 1 220px', width: 'auto' }} value={ajout} onChange={(e) => setAjout(e.target.value)}>
            <option value="">{data.candidats.length ? '+ ajouter un secouriste' : 'tout le monde est déjà dans la liste'}</option>
            {data.candidats.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button type="button" disabled={busy || !ajout}
            onClick={async () => { if (await ajouter({ userId: ajout })) setAjout(''); }} style={btn(C.brand)}>Ajouter</button>
        </div>
      )}
      <p style={{ fontSize: '0.8rem', color: C.mute, margin: '0.2rem 0 0' }}>
        Art. I.5-9 : recyclage annuel de 4 h minimum ; une séance manquée se rattrape pendant 12 mois,
        ensuite la personne n’est plus réputée compétente. Les responsables reçoivent un rappel 60 puis 30 jours avant.
      </p>

      <h2 style={titre}>Autres formations (cariste, sécurité…)</h2>
      {!data.autres.length && <div style={{ ...card, color: C.mute }}>Aucune autre formation notée pour l’instant.</div>}
      {data.autres.map(carte)}
      {canManage && (
        <div style={{ ...card, display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select style={{ ...inp, flex: '1 1 200px', width: 'auto' }} value={ajoutAutre.userId}
              onChange={(e) => setAjoutAutre((a) => ({ ...a, userId: e.target.value }))}>
              <option value="">+ ajouter une formation pour…</option>
              {data.personnes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select style={{ ...inp, flex: '1 1 200px', width: 'auto' }} value={ajoutAutre.type}
              onChange={(e) => setAjoutAutre((a) => ({ ...a, type: e.target.value }))}>
              {TYPES_AUTRES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select>
          </div>
          <input type="text" style={inp} value={ajoutAutre.intitule} maxLength={120}
            placeholder={ajoutAutre.type === 'AUTRE' ? 'laquelle ? (obligatoire, ex. nacelle)' : 'précision (facultatif, ex. chariot latéral)'}
            onChange={(e) => setAjoutAutre((a) => ({ ...a, intitule: e.target.value }))} />
          {ajoutAutre.userId && intituleManque && <div style={{ color: '#991B1B', fontSize: '0.86rem' }}>{MSG_INTITULE}</div>}
          <div>
            <button type="button" disabled={busy || !ajoutAutre.userId || intituleManque} style={btn(C.brand)}
              onClick={async () => {
                const ok = await ajouter({ userId: ajoutAutre.userId, type: ajoutAutre.type, intitule: ajoutAutre.intitule.trim() || null });
                if (ok) setAjoutAutre({ userId: '', type: 'CARISTE', intitule: '' });
              }}>
              Ajouter
            </button>
          </div>
        </div>
      )}
      <p style={{ fontSize: '0.8rem', color: C.mute, margin: '0.2rem 0 0' }}>
        La fin de validité est celle écrite sur le certificat ; un rappel part 60 puis 30 jours avant.
      </p>
    </div>
  );
}

